import {Segment, Speaker, Transcription} from "@/lib/types"
import {firestore, functions, httpsCallable} from "@/firebase/firebaseConfig";
import {
    collection, FieldValue,
    getDocs,
    onSnapshot,
    query,
    where,
} from "@firebase/firestore";
import {currentUser} from "@/firebase/firebaseAuth";
import {numberedMapToArray, parseFirebaseDate} from "@/firebase/firestoreUtil";
import {DocumentSnapshot} from "firebase/firestore";
import {getUserAndPermissions, useAdminPermissions} from "@/context/AuthContext";
import {getUserPermissions} from "@/lib/UserPermissions";
import {getAvailableVoices, getVoiceById} from "@/lib/Voice";


/**
 * Maps the Transcription document from Firestore to a Transcription object.
 * This conversion is necessary because the way segments and speakers are stored in Firestore is different from
 * the way they are stored in the Transcription object.
 * Within the webapp we deal with arrays of segments and speakers, but in Firestore we save them as maps.
 * @param {DocumentSnapshot} doc - The Firestore document snapshot
 * @return {Transcription | null} - The Transcription object
 */
export function mapTranscriptionFromFirestore(doc: DocumentSnapshot): Transcription | null {
    const data = doc.data()

    if (!data) {
        return null
    }

    const transcription: Transcription = {
        ...data,
        id: doc.id,
        userVideoVersion: data.userVideoVersion,
        userVideoId: data.userVideoId,
        creator: data.creator,
        language: data.language,
        pipelineUsed: data?.pipelineUsed,
        translated: data.translated,
        isSourceTranscript: data?.isSourceTranscript,
        readOnly: data?.readOnly,
        dubbingType: data?.dubbingType,
        dateCreated: parseFirebaseDate(data.dateCreated),
        lastUpdated: parseFirebaseDate(data.lastUpdated)
    } as Transcription

    // Check if `segments` is an array or a map and handle accordingly
    let segmentsArray = []
    if (data.segments) {
        segmentsArray = Array.isArray(data.segments)
            ? data.segments
            : numberedMapToArray(data.segments);
    }

    const segments: Segment[] = segmentsArray.map((segment: any) => {
        return {
            start: segment.start,
            end: segment.end,
            speaker: segment.speaker,
            text: segment.text,
            speakerUpdated: false, //segment.speakerUpdated,
            textUpdated: false //segment.textUpdated,
        };
    });

    // Check if `speakers` is an array or a map and handle accordingly
    let speakersArray = []
    if (data.speakers) {
        speakersArray = Array.isArray(data.speakers)
            ? data.speakers
            : numberedMapToArray(data.speakers);
    }

    const speakers: Speaker[] = speakersArray.map((speaker: any) => {
        const voice = getVoiceById(speaker.voice)
        return {
            id: speaker.id,
            label: speaker.label,
            voice: speaker.voice,
            voiceDisplayName: speaker.voiceDisplayName || voice?.displayName,
            voiceCountryCode: speaker.voiceCountryCode || voice?.countryCode,
            labelUpdated: false, //speaker.labelUpdated,
            voiceUpdated: false //speaker.voiceUpdated,
        };
    });

    transcription.segments = segments
    transcription.speakers = speakers

    return transcription
}

/**
 * Lists all Transcription objects for a given user video ID
 * @param userVideoId
 */
export function listTranscriptsByUserVideoId(userVideoId: string): Promise<Transcription[]> {
    return getUserAndPermissions().then(async ({uid, isAdmin}) => {
        console.log('listTranscriptsByUserVideoId: userVideoId', userVideoId, 'uid', uid)

        if (!uid || !userVideoId) {
            return [] as Transcription[]
        }
        const results = await getDocs(query(collection(firestore, "Transcriptions"),
            // where("creator", "==", uid),
            where("userVideoId", "==", userVideoId)));
        const transcripts: Transcription[] = [];
        results.forEach((doc) => {
            // const transcript = {id: doc.id, ...doc.data()} as Transcription
            const transcript = mapTranscriptionFromFirestore(doc);
            if (transcript) {
                transcripts.push(transcript);
            }
        });
        return transcripts;
    }).catch((error) => {
        console.error('Error listing transcripts:', error);
        throw new Error('Error listing transcripts');
    })
}

/**
 * Requests a new translations for a given user video ID and target language
 * This then generates a new Transcription object in Firestore
 * @param userVideoId
 * @param targetLanguage
 */
export function requestTranslation(userVideoId: string, targetLanguage: string): Promise<void> {
    const requestForTranslation = httpsCallable(functions, 'translation-request');
    return requestForTranslation({userVideoId, targetLanguage})
        .then((result) => {
            console.log('Translation requested for', targetLanguage)
        })
        .catch((error) => {
            console.error('Error requesting translation', error)
            throw new Error('Error requesting translation')
        })
}

/**
 * Subscribes to Transcription objects for a given user video ID
 * @param userVideoId
 * @param callback
 */
export async function subscribeToTranscripts(userVideoId: string, callback: (snapshot: any) => void) {
    const {uid, isAdmin} = await getUserAndPermissions()

    console.log('subscribeToTranscripts: userVideoId', userVideoId, 'uid', uid, 'isAdmin', isAdmin)

    let transcriptsQuery

    if (isAdmin) { //If the user is an admin, they can see all transcriptions
        transcriptsQuery = query(
            collection(firestore, 'Transcriptions'),
            where("userVideoId", "==", userVideoId)
        )
    } else { //Include the creator in the query for non-admins to meet Firestore security rules
        transcriptsQuery = query(
            collection(firestore, 'Transcriptions'),
            where("creator", "==", uid),
            where("userVideoId", "==", userVideoId)
        )
    }
    return onSnapshot(transcriptsQuery, (snapshot) => {
        callback(snapshot)
    }, (error) => {
        console.error('Error subscribing to transcripts:', error)
    })
}

/**
 * Save changes made to Transcription objects (e.g. segments, speakers)
 * @param userVideoId
 * @param transcriptions
 * @param requireRegeneration
 */
export function saveTranscriptions(userVideoId: string, transcriptions: Map<string, Transcription>, requireRegeneration: boolean): Promise<void> {
    const requestForTranslation = httpsCallable(functions, 'transcription-save')
    const transcriptionsObject = Object.fromEntries(transcriptions)
    return requestForTranslation({userVideoId, transcriptions: transcriptionsObject, requireRegeneration})
        .then((result) => {
            console.log('Transcriptions saved successfully')
        })
        .catch((error) => {
            console.error('Error saving transcriptions', error)
        })
}